import React, { useState, useEffect, ChangeEvent, useMemo } from 'react';
import { Button, Dialog, Hidden, IconButton, Stack, Switch, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useHistory } from 'react-router';
import AddIcon from '@mui/icons-material/Add';
import { DataTable } from '../DataTable';
import { useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { CommonLayout, ConfirmationDialog, HeaderSidenav } from '../commonComponents';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DPNameType } from '../../redux-store/types/api-types';
import { getDPNameList, updateDPName } from '../../redux-store/actions/userManagement';
import EditIcon from '@mui/icons-material/Edit';
import { Link as RouterLink } from 'react-router-dom';

export const AccessHeaderForBroker = (): JSX.Element => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  return (
    <>
      <Box display={'flex'} flexDirection={'column'}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: 14,
            color: 'primary.main',
            fontWeight: 500,
          }}
          component="span">
          Access
          <InfoOutlinedIcon
            color="info"
            fontSize="small"
            onClick={handleOpen}
            sx={{ position: 'relative', top: 4 }}
          />
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: 12,
            color: 'primary.main',
            fontWeight: 500,
          }}>
          Enable/Disable
        </Typography>
      </Box>
      <Dialog
        onClose={handleClose}
        open={open}
        sx={{
          '.MuiPaper-root ': {
            maxWidth: 550,
            p: { xs: 2, sm: '15px 20px 45px' },
            borderRadius: '10px',
            height: 'unset',
            overflowY: 'auto',
          },
        }}>
        <IconButton onClick={handleClose} sx={{ alignSelf: 'flex-end' }}>
          <CloseIcon fontSize="medium" />
        </IconButton>
        <Box sx={{ mb: 2, textAlign: 'center', '.MuiSvgIcon-root': { fontSize: '3.1875rem' } }}>
          <InfoOutlinedIcon color="info" fontSize="large" />
        </Box>
        <Box
          sx={{
            width: { xs: '100%', sm: '80%' },
            mx: 'auto',
            '.MuiTypography-root': {
              fontSize: 20,
              fontWeight: 500,
              textAlign: 'center',
            },
          }}>
          <Typography sx={{ color: 'secondary.main', lineHeight: '35px', mt: 2 }}>
            Please ensure that you have at least one active broker before disabling the broker.
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

export default function BrokerNameList({
  location,
}: {
  location: { state?: { brokerId?: number } };
}): JSX.Element {
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  // const [access, setAccess] = useState(false);
  const history = useHistory();
  const [brokerData, setBrokerData] = useState<DPNameType[]>([]);
  const [openDialog, setDialog] = useState<{ BrokerData: DPNameType } | null>(null);

  useEffect(() => {
    let isComponentActive = true;

    (async function () {
      try {
        setLoading(true);

        const res = (await dispatch(getDPNameList())) as unknown as DPNameType[];

        if (!isComponentActive) {
          return;
        }

        setBrokerData(res);
        setLoading(false);
      } catch (e) {
        setLoading(false);
        console.error((e as Error).message);
      }
    })();

    return () => {
      isComponentActive = false;
    };
  }, []);

  const AccessViewOrEditCell = [
    {
      header: AccessHeaderForBroker,
      renderCell: (row: DPNameType): JSX.Element => {
        return (
          <>
            <Stack
              sx={{
                alignItems: { xs: 'baseline', sm: 'center' },
                '.MuiSwitch-root': {
                  paddingLeft: { xs: '0px', sm: '12px' },
                  width: { xs: '52px', sm: '58px' },
                },
                '.MuiSwitch-switchBase': { paddingLeft: { xs: '0px', sm: '9px' } },
              }}>
              <Switch
                sx={{ '.MuiSwitch-switchBase': { color: 'black' } }}
                checked={row.isActive}
                onChange={() => setDialog({ BrokerData: row })}
                // disabled={!access}
              />
            </Stack>
          </>
        );
      },
    },
  ];
  const columns = [
    {
      header: 'Broker Name',
      renderCell: (row: DPNameType) => (
        <Typography
          variant="h5"
          sx={{
            color: '#2057A6',
            fontWeight: 500,
            fontSize: '14px',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
          }}
          // component={RouterLink}
          // to={{ pathname: 'edit-broker', state: { brokerId: row.id } }}
        >
          {row.brokerName || 'N/A'}
        </Typography>
      ),
    },
  ];

  const EditColumn = [
    {
      header: 'Edit',
      renderCell: (row: DPNameType): JSX.Element => (
        <IconButton
          // disabled={!access}
          color="primary"
          onClick={() =>
            history.push('edit-broker', {
              brokerId: row.id,
            })
          }>
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  const handlePageChange = (event: ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  return (
    // <CommonLayout>
    <>
      <Box
        sx={{
          display: 'flex',
          //flexWrap: 'wrap',
          // minHeight: 'calc(100vh - 43px)',
          overflow: 'auto',
          flexDirection: 'column',
          overflowX: 'hidden',
          minHeight: '100vh',
        }}>
        <HeaderSidenav />
        <Box
          component="main"
          sx={{
            mt: '65px',
            ml: { xs: 0, md: '169px' },
            p: { xs: 2, sm: 3, md: 5 },
            bgcolor: { xs: '#EEF4FB', md: 'rgba(238,244,251,0.5)' },
            minHeight: 'calc(100vh - 65px)',
            width: {
              xs: '100%',
              md: 'calc(100% - 169px)',
            },
            boxSizing: 'border-box',
          }}>
          <Box
            sx={{
              bgcolor: 'common.white',
              borderRadius: { xs: 2, md: 0 },
              p: { xs: 2, sm: 3, md: 4 },
              width: '100%',
              boxSizing: 'border-box',
            }}>
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: { xs: 16, sm: 18 },
                fontWeight: 500,
                mb: { xs: 2, md: 4 },
                p: { xs: '5px 15px', md: '' },
                bgcolor: '#e5f1f8',
                borderRadius: '10px',
              }}>
              Broker Name
            </Typography>

            <Box
              sx={{
                p: '20px 30px',
                bgcolor: 'common.white',
                mt: 1.5,
              }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  mb: 3,
                }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  sx={{
                    color: 'primary.main',
                    fontWeight: 500,
                    fontSize: { xs: 12, sm: 14 },
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => history.push('add-broker')}>
                  {window.innerWidth < 600 ? 'Add Broker' : 'Add Broker Name'}
                </Button>
              </Box>
              {/* </Box> */}

              {/* {useMemo(
                () => ( */}
              <Box
                sx={{
                  width: '100%',
                  overflow: 'hidden',
                }}>
                <DataTable
                  tableData={brokerData}
                  tableHeader={[...columns, ...AccessViewOrEditCell, ...EditColumn]}
                  currentPage={currentPage}
                  noOfPages={1}
                  isLoading={isLoading}
                  paginationAction={handlePageChange}
                  renderAdditionalRow={true}
                  tableBodyCustomStyles={{
                    '.MuiTableCell-root': {
                      borderBottom: 'none',
                      padding: { xs: '8px 4px', sm: '12px 8px' },
                      fontSize: { xs: 12, sm: 13, md: 14 },
                    },
                  }}
                  tableHeaderCustomStyles={{
                    '&:last-child th': {
                      border: 0,
                    },
                    '.MuiTableCell-head': {
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: { xs: 12, sm: 13, md: 14 },
                      fontWeight: 500,
                      color: 'primary.main',
                      padding: { xs: '8px 4px', sm: '12px 8px' },
                    },
                  }}
                />
              </Box>
              {/* ),
                [brokerData, currentPage, isLoading]
              )} */}
              <ConfirmationDialog
                message={`Are you sure you want to ${
                  openDialog?.BrokerData.isActive ? 'disable' : 'enable'
                } this Broker?`}
                open={openDialog !== null}
                setOpen={() => setDialog(null)}
                onSave={async () => {
                  try {
                    if (openDialog) {
                      const brokerId = Number(openDialog.BrokerData.id);
                      // Call your broker update API/action here
                      const updatedBroker = await dispatch(
                        updateDPName({
                          ...openDialog.BrokerData,
                          isActive: !openDialog.BrokerData.isActive,
                        })
                      );
                      setBrokerData((prev) =>
                        prev.map((broker) =>
                          Number(broker.id) === brokerId
                            ? {
                                ...broker,
                                ...updatedBroker,
                              }
                            : broker
                        )
                      );
                    }
                  } catch (e) {
                    console.error((e as Error).message);
                  } finally {
                    setDialog(null);
                  }
                }}
                onCancel={() => setDialog(null)}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {/* </CommonLayout> */}
    </>
  );
}
